/**
 * Origin / destination selection rules, in one place.
 *
 * The lane pickers disagree on presentation — MultiLocationInput is a type-ahead with inline chips, the
 * Box Truck LanePicker is a state grid — but they must not disagree on what a selection MEANS, because
 * both feed the same search request. A city carries coordinates and is searched as a point with a radius;
 * states and zones are searched as areas. Mixing the two has no meaning server-side, which is why picking
 * a city REPLACES the selection while states and zones stack.
 *
 * Kept next to the LocationData type rather than inside either picker so a third caller cannot quietly
 * fork the rules.
 *
 * `selectCity` and `addCity` are the one place the two pickers deliberately differ, and the difference is
 * a capability, not a taste: the Box Truck board runs ONE SEARCH PER POINT (`buildRequests` fans a lane
 * out per pinned chip, which is what picking several drivers already does), so several points there are
 * several searches merged into one board. Load Hub sends a single request, where a second point has
 * nowhere to go. Both still refuse to MIX a point with an area.
 */

import type { StateLocationData, ZoneLocationData } from 'utils/location-search/LocationDataSearchV2'
import type { LocationData } from '../types/filters'

/** Each LocationData is one of three shapes: city (city + stateProv + coords), state (stateProv), zone (zoneCode). */
export const isZoneLocation = (loc: LocationData): boolean => !!loc.zoneCode
export const isCityLocation = (loc: LocationData): boolean => !loc.zoneCode && !!loc.city

/**
 * Stable id for a selected location — used for React keys, removal, and de-duplication. Must be
 * deterministic across renders: a random fallback re-keys the chip every render and forces a remount loop
 * (focus loss, input reset). See PR #7969 r3246275439.
 */
export function locationChipId(loc: LocationData): string {
    if (loc.zoneCode) return `zone-${loc.zoneCode}`
    // A ZIP-resolved point is identified BY ITS ZIP: 60601 and 60290 both answer "Chicago, IL", and
    // without this the second one is deduplicated away as the first — which, on a board that searches
    // each point, silently drops a search the operator asked for. Chips without a ZIP are unchanged.
    if (loc.postalCode) return `zip-${loc.postalCode}`
    if (loc.city && loc.stateProv) return `city-${loc.city}-${loc.stateProv}`
    if (loc.stateProv) return `state-${loc.stateProv}`
    return `loc-${loc.city ?? ''}-${loc.stateProv ?? ''}-${loc.zoneCode ?? ''}`
}

/** Compact label — "60601", "Chicago, IL", "TX", "ZCA". */
export function locationChipLabel(loc: LocationData): string {
    if (loc.zoneCode) return loc.zoneCode
    // The ZIP, not the city it resolved to: it is what the operator typed, it is shorter, and two ZIPs in
    // one city would otherwise read as the same chip twice.
    if (loc.postalCode) return loc.postalCode
    if (loc.city && loc.stateProv) return `${loc.city}, ${loc.stateProv}`
    if (loc.stateProv) return loc.stateProv
    return ''
}

/** The shape every point pick produces. `postalCode` is set only when the point came from a ZIP. */
interface CityPick {
    city: string
    stateCode: string
    latitude: number
    longitude: number
    postalCode?: string
}

const toCityLocation = (city: CityPick): LocationData => ({
    city: city.city,
    stateProv: city.stateCode,
    latitude: city.latitude,
    longitude: city.longitude,
    postalCode: city.postalCode ?? '',
})

/** A city is exclusive: it replaces whatever was selected, states and zones included. */
export const selectCity = (city: CityPick): LocationData[] => [toCityLocation(city)]

/**
 * Add a point, keeping the points already picked. For callers that search EACH point — see the note at
 * the top of this file; on a single-request caller this would quietly search only one of them.
 *
 * Still refuses the mix: states and zones are dropped, exactly as `selectCity` drops them. Idempotent by
 * chip id, so re-picking the same ZIP or city is a no-op rather than a duplicate lane.
 *
 * `max` bounds what the caller will SPEND, because on a fan-out caller each point is a whole search —
 * its own SSE subscription, its own Sylectus query, its own DAT call against a token pool every operator
 * shares. A per-lookup cap does not bound it: twelve pasted ZIPs added twice are twenty-four lanes. The
 * extra point is REFUSED rather than dropped silently; the caller is expected to say so, since a point
 * that vanishes on its way into the field is indistinguishable from one that never resolved.
 */
export function addCity(values: LocationData[], city: CityPick, max?: number): LocationData[] {
    const base = values.filter(isCityLocation)
    const addition = toCityLocation(city)
    const id = locationChipId(addition)
    if (base.some((v) => locationChipId(v) === id)) return base
    if (max != null && base.length >= max) return base
    return [...base, addition]
}

/** True when this exact state (not a city in it, not a zone containing it) is already selected. */
export const hasState = (values: LocationData[], stateCode: string): boolean => values.some((v) => !v.city && !v.zoneCode && v.stateProv === stateCode)

/** True when this zone is already selected. */
export const hasZone = (values: LocationData[], zoneCode: string): boolean => values.some((v) => v.zoneCode === zoneCode)

/** Add a state. Idempotent, stacks on other states/zones, and replaces a city — which cannot coexist with an area. */
export function addState(values: LocationData[], stateCode: string): LocationData[] {
    const base = values.some(isCityLocation) ? [] : values
    if (hasState(base, stateCode)) return base
    return [...base, { stateProv: stateCode }]
}

/** Add a zone. Same stacking rule as a state — a zone IS a group of states. */
export function addZone(values: LocationData[], zone: { zoneCode: string; states: string }): LocationData[] {
    const base = values.some(isCityLocation) ? [] : values
    if (hasZone(base, zone.zoneCode)) return base
    return [...base, { zoneCode: zone.zoneCode, zoneStates: zone.states }]
}

/** Grid semantics: clicking a selected tile clears it, clicking an unselected one adds it. */
export const toggleState = (values: LocationData[], stateCode: string): LocationData[] =>
    hasState(values, stateCode) ? values.filter((v) => v.city || v.zoneCode || v.stateProv !== stateCode) : addState(values, stateCode)

/** How many of these states are selected as states — a region pill shows none, some or all. */
export const countSelectedStates = (values: LocationData[], stateCodes: readonly string[]): number => stateCodes.filter((code) => hasState(values, code)).length

/**
 * Region semantics: a region is a shortcut for its states, never a chip of its own. Picking it adds every
 * state it is missing; picking it when all are already selected clears them. Other states stay put.
 */
export function toggleStates(values: LocationData[], stateCodes: readonly string[]): LocationData[] {
    if (countSelectedStates(values, stateCodes) < stateCodes.length) return stateCodes.reduce((acc, code) => addState(acc, code), values)
    const cleared = new Set(stateCodes)
    return values.filter((v) => v.city || v.zoneCode || !v.stateProv || !cleared.has(v.stateProv))
}

export const toggleZone = (values: LocationData[], zone: { zoneCode: string; states: string }): LocationData[] =>
    hasZone(values, zone.zoneCode) ? values.filter((v) => v.zoneCode !== zone.zoneCode) : addZone(values, zone)

/** Remove one selected location by its chip id. */
export const removeLocation = (values: LocationData[], id: string): LocationData[] => values.filter((v) => locationChipId(v) !== id)

/**
 * Bulk paste — "TX,CA,FL" or a pasted zone list. Additive and de-duplicated, and (like a single pick)
 * drops a city selection rather than mixing point and area search.
 */
export function addBulk(values: LocationData[], items: (StateLocationData | ZoneLocationData)[]): LocationData[] {
    if (items.length === 0) return values
    const additions: LocationData[] = items.map((item) =>
        'zoneCode' in item && item.zoneCode
            ? { zoneCode: item.zoneCode, zoneStates: (item as ZoneLocationData).states }
            : { stateProv: (item as StateLocationData).stateCode }
    )
    const base = values.some(isCityLocation) ? [] : values
    const seen = new Set(base.map(locationChipId))
    const merged = [...base]
    for (const addition of additions) {
        const id = locationChipId(addition)
        if (seen.has(id)) continue
        merged.push(addition)
        seen.add(id)
    }
    return merged
}
